// ── Local draft mirror (mobile Track A, wave 1) ───────────────────────────────
// Drafts auto-save to the `drafts` table every 30 s — which silently saves
// nothing when the phone has no signal, so a half-filled form died with the
// app if the OS reclaimed it in a dead zone. This module keeps a same-shape
// copy in IndexedDB on every save. The server stays the cross-device source
// of truth; the mirror is the on-device safety net, and whichever copy has
// the newer `updated_at` wins on restore (loadDraftData in app.js).
//
// Every entry point swallows storage failures: a broken IndexedDB must
// degrade to yesterday's behaviour (server-only drafts), never break a form.

const DB_NAME = 'ordinex-drafts';
const STORE   = 'drafts';

function _open() {
  return new Promise((resolve, reject) => {
    let req;
    try { req = indexedDB.open(DB_NAME, 1); }
    catch (e) { reject(e); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

function _key(userId, formType, scope) {
  return `${userId}|${formType}|${scope}`;
}

// Store the draft. Resolves true when the device copy landed, false on any
// storage failure — the caller uses that to word the "saved" banner honestly.
export async function putLocalDraft(userId, formType, scope, formData) {
  if (!userId || !formType) return false;
  try {
    const db = await _open();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r  = tx.objectStore(STORE).put({
        key: _key(userId, formType, scope),
        form_data: formData || {},
        updated_at: new Date().toISOString(),
      });
      r.onsuccess = () => resolve();
      r.onerror   = () => reject(r.error);
    });
    return true;
  } catch (_) { return false; }
}

// Same row shape the server select returns ({ form_data, updated_at }), so
// the restore path treats both sources identically. Null when absent/broken.
export async function getLocalDraft(userId, formType, scope) {
  if (!userId || !formType) return null;
  try {
    const db = await _open();
    const rec = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const r  = tx.objectStore(STORE).get(_key(userId, formType, scope));
      r.onsuccess = () => resolve(r.result || null);
      r.onerror   = () => reject(r.error);
    });
    if (!rec) return null;
    return { form_data: rec.form_data || {}, updated_at: rec.updated_at || null };
  } catch (_) { return null; }
}

export async function removeLocalDraft(userId, formType, scope) {
  if (!userId || !formType) return;
  try {
    const db = await _open();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const r  = tx.objectStore(STORE).delete(_key(userId, formType, scope));
      r.onsuccess = () => resolve();
      r.onerror   = () => reject(r.error);
    });
  } catch (_) { /* nothing to clean, or storage is broken — either way, done */ }
}

// Pick the survivor between the server row and the device mirror. Newer
// `updated_at` wins; a copy with no timestamp loses to one that has it.
export function newerDraft(server, local) {
  if (!server) return local || null;
  if (!local)  return server;
  const s = Date.parse(server.updated_at || '') || 0;
  const l = Date.parse(local.updated_at  || '') || 0;
  return l > s ? local : server;
}
